'use client';

import { useEffect, useState } from 'react';
import { DomainSuggestion } from '../lib/types';

interface Step3Props {
  domains: DomainSuggestion[];
  selectedDomain: string;
  onSelect: (domain: string) => void;
  onDomainsUpdate: (domains: DomainSuggestion[]) => void;
  onNext: () => void;
  onBack: () => void;
}

export default function Step3DomainResults({
  domains,
  selectedDomain,
  onSelect,
  onDomainsUpdate,
  onNext,
  onBack,
}: Step3Props) {
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function checkDomains() {
      setChecking(true);
      const updated = [...domains];

      for (let i = 0; i < updated.length; i++) {
        if (cancelled) return;
        try {
          const res = await fetch(
            `/api/check-domain?domain=${encodeURIComponent(updated[i].domain)}`
          );
          const data = await res.json();
          updated[i] = { ...updated[i], available: data.available };
        } catch {
          updated[i] = { ...updated[i], available: false };
        }
        onDomainsUpdate([...updated]);
      }

      setChecking(false);
    }

    if (domains.some((d) => d.available === null)) {
      checkDomains();
    } else {
      setChecking(false);
    }

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const availableCount = domains.filter((d) => d.available === true).length;

  return (
    <div className="animate-fade-in max-w-lg mx-auto">
      <h2 className="text-2xl font-bold mb-2">Choisissez votre domaine</h2>
      <p className="text-muted mb-6">
        {checking
          ? 'Vérification de la disponibilité en cours...'
          : `${availableCount} domaine${availableCount > 1 ? 's' : ''} disponible${availableCount > 1 ? 's' : ''}`}
      </p>

      <div className="space-y-2.5">
        {domains.map((d, i) => {
          const isChecking = d.available === null;
          const isAvailable = d.available === true;
          const isSelected = selectedDomain === d.domain;

          return (
            <button
              key={d.domain}
              onClick={() => isAvailable && onSelect(d.domain)}
              disabled={!isAvailable}
              className={`w-full flex items-center justify-between px-4 py-3.5 rounded-lg border-2 transition-all duration-200 text-left cursor-pointer disabled:cursor-not-allowed animate-slide-in ${
                isSelected
                  ? 'border-primary bg-primary-light/50 shadow-sm'
                  : isAvailable
                    ? 'border-border hover:border-primary/50 hover:bg-gray-50'
                    : 'border-border bg-gray-50 opacity-60'
              }`}
              style={{ animationDelay: `${i * 60}ms` }}
            >
              <div className="flex items-center gap-3">
                <div
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                    isSelected
                      ? 'border-primary bg-primary'
                      : 'border-border'
                  }`}
                >
                  {isSelected && (
                    <div className="w-2 h-2 bg-white rounded-full" />
                  )}
                </div>
                <span className={`font-mono text-sm sm:text-base ${!isAvailable && !isChecking ? 'line-through' : ''}`}>
                  {d.domain}
                </span>
              </div>

              <span className="shrink-0 ml-2">
                {isChecking ? (
                  <span className="text-muted text-sm flex items-center gap-1.5">
                    <span className="inline-block w-4 h-4 border-2 border-muted/30 border-t-muted rounded-full animate-spin" />
                  </span>
                ) : isAvailable ? (
                  <span className="text-success text-sm font-medium animate-pulse-check">
                    &#10003; Disponible
                  </span>
                ) : (
                  <span className="text-danger text-sm">&#10007; Pris</span>
                )}
              </span>
            </button>
          );
        })}
      </div>

      <div className="flex gap-3 mt-8">
        <button
          onClick={onBack}
          className="px-6 py-3 rounded-lg border border-border text-muted hover:bg-gray-50 transition-colors cursor-pointer"
        >
          Retour
        </button>
        <button
          onClick={onNext}
          disabled={!selectedDomain}
          className="flex-1 bg-primary hover:bg-primary-hover disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-lg transition-colors cursor-pointer"
        >
          Configurer les emails
        </button>
      </div>
    </div>
  );
}